import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Brain,
  ChevronDown,
  ChevronUp,
  FileText,
  Globe,
  Loader2,
  MessageSquare,
  Plus,
  Send,
  Square,
  Trash2,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useDocumentViewer } from "@/contexts/DocumentViewerContext";
import {
  useChatSessions,
  useCreateChatSession,
  useDeleteChatSession,
  useSessionMessages,
  useStreamChatMessage,
  useUpdateChatSessionFilters,
  useUpdateChatSessionWebSearch,
} from "@/hooks/useChat";
import {
  stopStream,
  useChatStream,
  useIsStreaming,
} from "@/services/chatStreamStore";
import { useDocuments } from "@/hooks/useDocumentMetadata";
import { useProjectCustomFields } from "@/hooks/useProjectCustomFields";
import { useProject, useProjectDocuments } from "@/hooks/useProjects";
import {
  AppliedFilters,
  ChatCitation,
  ChatMessage,
  ChatSession,
  WebCitation,
} from "@/services/chatApi";
import type { ProjectDocumentItem } from "@/services/projectsApi";
import type { Document } from "@/types/models";
import ProjectChatFilters, {
  applyFilters,
  buildFilterFields,
  type FilterSelection,
} from "@/components/ProjectChatFilters";

interface ProjectChatProps {
  projectId: string;
  isAdvancedRag?: boolean;
}

function senderLabel(message: ChatMessage, currentEmail: string | undefined): string {
  if (message.role === "assistant") return "Assistant";
  if (message.senderEmail && currentEmail && message.senderEmail === currentEmail) return "You";
  return message.senderEmail ?? "Member";
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// Compact relative age for the session list ("3h ago", "1w ago"), matching
// the density of a chat-history sidebar rather than a full timestamp.
// { upto: switch to the next unit once the age reaches this many seconds,
//   per: seconds represented by one unit of `label` }
const RELATIVE_TIME_UNITS = [
  { upto: 60, per: 1, label: "s" },
  { upto: 3600, per: 60, label: "m" },
  { upto: 86400, per: 3600, label: "h" },
  { upto: 604800, per: 86400, label: "d" },
  { upto: 2629800, per: 604800, label: "w" },
  { upto: 31557600, per: 2629800, label: "mo" },
  { upto: Infinity, per: 31557600, label: "y" },
];

function formatRelativeAge(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffSeconds = Math.max(0, (Date.now() - then) / 1000);

  const unit = RELATIVE_TIME_UNITS.find(({ upto }) => diffSeconds < upto)
    ?? RELATIVE_TIME_UNITS[RELATIVE_TIME_UNITS.length - 1];
  if (unit.label === "s") return "just now";
  return `${Math.floor(diffSeconds / unit.per)}${unit.label} ago`;
}

const MAX_FILE_PICKER_RESULTS = 8;

// content-intelligence stores `sourcepage` as either a bare number or a path
// fragment like "<filename>#page=N". Return the actual page integer (or null
// when it can't be derived) so display and PDF deep-link both use the right
// value.
function extractPageNumber(sourcepage: string | undefined | null): number | null {
  if (!sourcepage) return null;
  const fragment = /#page=(\d+)/.exec(sourcepage);
  if (fragment) return Number.parseInt(fragment[1], 10);
  const bare = /^\s*(\d+)\s*$/.exec(sourcepage);
  return bare ? Number.parseInt(bare[1], 10) : null;
}

// Matches inline citation markers [N], [N, M], etc. Must stay in sync with the
// backend's filter so the rendered pills line up with what's persisted.
const CITATION_MARKER_RE = /\[(\d+(?:\s*,\s*\d+)*)\]/g;

// Markdown link prefix used to flag citation links inside react-markdown output.
const CITATION_HREF_PREFIX = "#cite-";

// react-markdown passes a link's rendered content as `children`, which may
// be a plain string or an array of nodes (rare for a bare "[1]" link, but
// handle it defensively rather than assuming a single string).
function childrenToText(children: React.ReactNode): string {
  return Array.isArray(children) ? children.join("") : String(children ?? "");
}

function linkifyCitations(text: string): string {
  return text.replace(CITATION_MARKER_RE, (_match, group: string) =>
    group
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean)
      .map((id) => `[${id}](${CITATION_HREF_PREFIX}${id})`)
      .join(""),
  );
}

/**
 * If the cursor sits inside a "@…" token (no whitespace between @ and cursor),
 * return the fragment after the @. Otherwise null. Mirrors GAIL3's behavior so
 * the trigger and dismiss rules feel identical to users moving between apps.
 */
function getAtQuery(text: string, cursor: number): string | null {
  const before = text.slice(0, cursor);
  const atIdx = before.lastIndexOf("@");
  if (atIdx === -1) return null;
  const fragment = before.slice(atIdx + 1);
  if (/\s/.test(fragment)) return null;
  return fragment;
}

const ProjectChat = ({ projectId, isAdvancedRag = false }: ProjectChatProps) => {
  const { user } = useAuth();
  const { data: sessions = [], isLoading: sessionsLoading } = useChatSessions(projectId);
  const createSession = useCreateChatSession(projectId);
  const deleteSession = useDeleteChatSession(projectId);

  // Build a filename → Document lookup scoped to the project's active documents
  // so citation chips can open the existing DocumentViewerSheet (mounted at App
  // level) and highlight the chunk text in the PDF.
  const { data: projectDocs = [] } = useProjectDocuments(projectId);
  const { data: allDocs = [] } = useDocuments();
  const { data: customFieldDefs = [] } = useProjectCustomFields(projectId);
  const { data: project } = useProject(projectId);
  const visibleFilterKeys = project?.visibleChatFilterKeys ?? null;

  // Active project docs are the universe both for citation resolution and
  // for the metadata filter panel — keep them computed once.
  const activeProjectDocs = useMemo(
    () => projectDocs.filter((p) => p.isActive),
    [projectDocs],
  );

  const docsById = useMemo(() => {
    const map = new Map<string, Document>();
    for (const doc of allDocs) map.set(doc.id, doc);
    return map;
  }, [allDocs]);

  const activeDocumentNames = useMemo(
    () =>
      activeProjectDocs
        .map((p) => docsById.get(p.documentId)?.filename)
        .filter((name): name is string => !!name),
    [activeProjectDocs, docsById],
  );

  const documentsByFilename = useMemo(() => {
    const map = new Map<string, Document>();
    for (const pdoc of activeProjectDocs) {
      const doc = docsById.get(pdoc.documentId);
      if (doc && doc.filename) map.set(doc.filename, doc);
    }
    return map;
  }, [activeProjectDocs, docsById]);

  const filterFields = useMemo(() => {
    const all = buildFilterFields({
      activeProjectDocs,
      docsById,
      customFieldDefs,
    });
    // null means "no admin override" → show all that have values. An
    // explicit array (even empty) restricts the panel to that subset.
    if (visibleFilterKeys === null) return all;
    const allowed = new Set(visibleFilterKeys);
    return all.filter((f) => allowed.has(f.key));
  }, [activeProjectDocs, docsById, customFieldDefs, visibleFilterKeys]);

  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);

  // Auto-select the most-recently-updated session when the list loads or when
  // the current selection disappears (e.g. someone deleted it).
  useEffect(() => {
    if (sessionsLoading) return;
    if (activeSessionId && sessions.some((s) => s.id === activeSessionId)) return;
    setActiveSessionId(sessions[0]?.id ?? null);
  }, [sessionsLoading, sessions, activeSessionId]);

  const activeSession = useMemo<ChatSession | null>(
    () => sessions.find((s) => s.id === activeSessionId) ?? null,
    [sessions, activeSessionId],
  );

  const handleNewSession = async () => {
    const session = await createSession.mutateAsync({});
    setActiveSessionId(session.id);
  };

  const handleDeleteSession = async (sessionId: string) => {
    // A background stream for a session that no longer exists has nowhere to
    // land — its assistant row would be cascade-deleted anyway.
    stopStream(sessionId);
    await deleteSession.mutateAsync({ sessionId });
    if (activeSessionId === sessionId) setActiveSessionId(null);
  };

  return (
    <Card className="flex min-h-0 flex-1 overflow-hidden">
      <div className="flex w-64 flex-col border-r bg-muted/30">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-medium">Sessions</span>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2"
            onClick={handleNewSession}
            disabled={createSession.isPending}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <ScrollArea className="flex-1 [&_[data-radix-scroll-area-viewport]>div]:!block">
          {sessionsLoading ? (
            <div className="p-4 text-sm text-muted-foreground">Loading…</div>
          ) : sessions.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">
              No sessions yet. Start a new chat to collaborate with project members.
            </div>
          ) : (
            <ul className="py-1">
              {sessions.map((session) => {
                const isActive = session.id === activeSessionId;
                return (
                  <li key={session.id}>
                    <div
                      className={cn(
                        "group flex w-full items-center gap-1 pr-2 text-sm hover:bg-accent hover:text-accent-foreground",
                        isActive && "bg-accent text-accent-foreground",
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => setActiveSessionId(session.id)}
                        className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left"
                      >
                        <MessageSquare
                          className={cn(
                            "h-4 w-4 shrink-0 group-hover:text-accent-foreground/80",
                            isActive ? "text-accent-foreground/80" : "text-muted-foreground",
                          )}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate font-medium">{session.title}</span>
                            <StreamingDot sessionId={session.id} />
                            {session.hasWebSearch && (
                              <Globe
                                className={cn(
                                  "h-3 w-3 shrink-0",
                                  isActive ? "text-white" : "text-blue-600 dark:text-blue-400",
                                )}
                                aria-label="Used web search"
                                title="Used web search"
                              />
                            )}
                          </div>
                          <div
                            className={cn(
                              "flex items-center gap-1 truncate text-xs group-hover:text-accent-foreground/80",
                              isActive ? "text-accent-foreground/80" : "text-muted-foreground",
                            )}
                          >
                            <span className="truncate">{session.createdByEmail ?? "Unknown"}</span>
                            <span>·</span>
                            <span className="shrink-0">{formatRelativeAge(session.createdAt)}</span>
                          </div>
                        </div>
                      </button>
                      {session.isOwner && (
                        <button
                          type="button"
                          onClick={() => handleDeleteSession(session.id)}
                          className={cn(
                            "invisible shrink-0 rounded p-1 hover:bg-destructive/10 hover:text-destructive group-hover:visible group-hover:text-accent-foreground/80",
                            isActive ? "text-accent-foreground/80" : "text-muted-foreground",
                          )}
                          aria-label="Delete session"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </ScrollArea>
      </div>

      {activeSession ? (
        <ChatSessionView
          // Key on session.id so React unmounts/remounts the view (and its
          // per-session filter state) cleanly when the active session changes
          // — no stale selections leaking between sessions.
          key={activeSession.id}
          session={activeSession}
          projectId={projectId}
          isAdvancedRag={isAdvancedRag}
          currentUserEmail={user?.email}
          documentsByFilename={documentsByFilename}
          activeProjectDocs={activeProjectDocs}
          docsById={docsById}
          filterFields={filterFields}
        />
      ) : (
        <div className="flex flex-1 items-center justify-center p-6 text-center text-muted-foreground">
          {sessionsLoading
            ? "Loading…"
            : "Start a new chat to ask grounded questions over this project's documents."}
        </div>
      )}
    </Card>
  );
};

// Sidebar marker for a session whose reply is still streaming. Its own
// component so it can subscribe to just that session's pending flag — the
// hook returns a boolean, so it re-renders twice per turn, not once per token.
const StreamingDot = ({ sessionId }: { sessionId: string }) => {
  const streaming = useIsStreaming(sessionId);
  if (!streaming) return null;
  return (
    <span
      className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-brand-teal"
      aria-label="Response still generating"
      title="Response still generating"
    />
  );
};

interface ChatSessionViewProps {
  session: ChatSession;
  projectId: string;
  isAdvancedRag?: boolean;
  currentUserEmail: string | undefined;
  documentsByFilename: Map<string, Document>;
  activeProjectDocs: ProjectDocumentItem[];
  docsById: Map<string, Document>;
  filterFields: ReturnType<typeof buildFilterFields>;
}

// Convert the JSON-friendly Record<string, string[]> persisted on the session
// into the runtime Record<string, Set<string>> the panel uses.
function fromPersistedFilters(
  persisted: Record<string, string[]> | null | undefined,
): FilterSelection {
  if (!persisted) return {};
  const out: FilterSelection = {};
  for (const [key, values] of Object.entries(persisted)) {
    if (Array.isArray(values) && values.length > 0) {
      out[key] = new Set(values);
    }
  }
  return out;
}

function toPersistedFilters(
  selection: FilterSelection,
): Record<string, string[]> | null {
  const out: Record<string, string[]> = {};
  for (const [key, values] of Object.entries(selection)) {
    if (values.size > 0) out[key] = Array.from(values);
  }
  return Object.keys(out).length === 0 ? null : out;
}

// Debounce window for filter-save POSTs. Tight enough that switching away
// from the session almost always lands the save, loose enough that a burst
// of checkbox toggles collapses into one round trip.
const FILTER_SAVE_DEBOUNCE_MS = 400;

const ChatSessionView = ({
  session,
  projectId,
  isAdvancedRag = false,
  currentUserEmail,
  documentsByFilename,
  activeProjectDocs,
  docsById,
  filterFields,
}: ChatSessionViewProps) => {
  const totalActiveDocs = activeProjectDocs.length;
  const { data: messages = [], isLoading } = useSessionMessages(session.id);
  const [draft, setDraft] = useState("");
  // Lives in chatStreamStore, not here — so unmounting this view (wizard step,
  // session switch, route change) no longer interrupts the response.
  const streamState = useChatStream(session.id);
  const [atQuery, setAtQuery] = useState<string | null>(null);
  const [pickerIndex, setPickerIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);

  // Per-session filter selection. Initialised from the session's saved
  // metadataFilters on first mount — the keyed remount in ProjectChat
  // guarantees this fires every time the active session changes.
  const [filterSelection, setFilterSelection] = useState<FilterSelection>(() =>
    fromPersistedFilters(session.metadataFilters),
  );

  // Drop selected values that no longer exist on any active doc (e.g. a doc
  // was deactivated, or the admin hid a filter). Keeps the panel and the
  // matched-id list in sync with current data.
  useEffect(() => {
    setFilterSelection((prev) => {
      let dirty = false;
      const next: FilterSelection = {};
      for (const [key, values] of Object.entries(prev)) {
        const field = filterFields.find((f) => f.key === key);
        if (!field) {
          dirty = true;
          continue;
        }
        const known = new Set(field.values.map((v) => v.value));
        const kept = new Set([...values].filter((v) => known.has(v)));
        if (kept.size !== values.size) dirty = true;
        if (kept.size > 0) next[key] = kept;
      }
      return dirty ? next : prev;
    });
  }, [filterFields]);

  const matchedDocumentIds = useMemo(
    () => applyFilters(filterFields, filterSelection, activeProjectDocs, docsById),
    [filterFields, filterSelection, activeProjectDocs, docsById],
  );


  // Persist selection changes back to the session, debounced. Skip the very
  // first effect run when the selection already matches what was loaded
  // from the session — avoids a redundant POST on mount.
  const updateFilters = useUpdateChatSessionFilters(projectId);
  const initialPersistedRef = useRef(session.metadataFilters);
  const skipNextSaveRef = useRef(true);
  useEffect(() => {
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }
    const payload = toPersistedFilters(filterSelection);
    const handle = window.setTimeout(() => {
      // Bail if the active session changed (or unmounted) before the
      // debounce fired — React will have already torn this view down.
      if (payload === initialPersistedRef.current) return;
      updateFilters.mutate({ sessionId: session.id, metadataFilters: payload });
    }, FILTER_SAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
    // updateFilters identity is stable across renders (React Query hook); we
    // intentionally exclude it to keep this effect debounce-only on selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSelection, session.id]);

  // Per-session opt-in for native web search. Seeded from the session's
  // persisted value; toggling fires the PATCH immediately (unlike the
  // filter panel's debounced save — a switch click is already a discrete,
  // deliberate action, not continuous typing).
  const [webSearchEnabled, setWebSearchEnabled] = useState(session.webSearchEnabled);
  const updateWebSearch = useUpdateChatSessionWebSearch(projectId);
  const handleWebSearchToggle = useCallback(
    (checked: boolean) => {
      setWebSearchEnabled(checked);
      updateWebSearch.mutate({ sessionId: session.id, webSearchEnabled: checked });
    },
    [session.id, updateWebSearch],
  );

  const { stream, cancel } = useStreamChatMessage(session.id, projectId, isAdvancedRag);

  // Deliberately no unmount cleanup: an in-flight stream must outlive this
  // view. It ends when it finishes, when the user presses Stop, or when the
  // session is deleted.

  // "Stick to bottom" auto-scroll: only snap to the latest content when the
  // user was already at (or near) the bottom. Otherwise every streamed token
  // would force-scroll the view and make it impossible to scroll up while a
  // response is rendering. handleScroll tracks the user's position on every
  // scroll (manual or programmatic); the effect below only acts on it.
  const stickToBottomRef = useRef(true);
  const SCROLL_BOTTOM_THRESHOLD_PX = 80;

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distanceFromBottom < SCROLL_BOTTOM_THRESHOLD_PX;
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (el && stickToBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [
    messages.length,
    streamState.streamingText,
    streamState.streamingThinking,
    streamState.pending,
  ]);

  const allFilenames = useMemo(
    () => Array.from(documentsByFilename.keys()).sort((a, b) => a.localeCompare(b)),
    [documentsByFilename],
  );

  const filteredFilenames = useMemo(() => {
    if (atQuery === null) return [];
    const q = atQuery.toLowerCase();
    return allFilenames
      .filter((name) => name.toLowerCase().includes(q))
      .slice(0, MAX_FILE_PICKER_RESULTS);
  }, [allFilenames, atQuery]);

  const pickerOpen = atQuery !== null && filteredFilenames.length > 0;

  const closePicker = useCallback(() => {
    setAtQuery(null);
    setPickerIndex(0);
  }, []);

  const handlePickFile = useCallback(
    (filename: string) => {
      const ta = textareaRef.current;
      const cursor = ta?.selectionStart ?? draft.length;
      const before = draft.slice(0, cursor);
      const atIdx = before.lastIndexOf("@");
      if (atIdx === -1) {
        closePicker();
        return;
      }
      const insertion = `@${filename} `;
      const newValue = draft.slice(0, atIdx) + insertion + draft.slice(cursor);
      setDraft(newValue);
      closePicker();
      requestAnimationFrame(() => {
        const node = textareaRef.current;
        if (!node) return;
        const pos = atIdx + insertion.length;
        node.focus();
        node.setSelectionRange(pos, pos);
      });
    },
    [draft, closePicker],
  );

  const handleSend = async () => {
    const content = draft.trim();
    if (!content || streamState.pending) return;
    if (matchedDocumentIds.length === 0) return;

    // Snapshot the filter selection (with current labels) so this turn's
    // applied-filters footer renders correctly later even if a custom
    // field gets renamed or the project's saved filters change.
    const items = filterFields
      .filter((f) => (filterSelection[f.key]?.size ?? 0) > 0)
      .map((f) => ({
        key: f.key,
        label: f.label,
        values: Array.from(filterSelection[f.key]),
      }));
    const appliedFilters: AppliedFilters | null = items.length === 0
      ? null
      : {
          items,
          matchedCount: matchedDocumentIds.length,
          totalCount: totalActiveDocs,
        };

    setDraft("");
    closePicker();
    // A fresh send should always follow the new response, even if the user
    // had scrolled up while reading earlier history.
    stickToBottomRef.current = true;
    // If the message contains @filename mentions that all resolve to known
    // project documents, scope the query to only those documents instead of
    // the full filtered set so irrelevant files are not cited.
    const mentionIds = parseAtMentionIds(content, documentsByFilename);
    const effectiveDocIds = mentionIds ?? matchedDocumentIds;
    try {
      await stream(content, effectiveDocIds, appliedFilters);
    } catch {
      setDraft(content);
    }
  };

  const filtersActive = matchedDocumentIds.length !== totalActiveDocs;
  const noMatches = matchedDocumentIds.length === 0;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (pickerOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setPickerIndex((i) => (i + 1) % filteredFilenames.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setPickerIndex(
          (i) => (i - 1 + filteredFilenames.length) % filteredFilenames.length,
        );
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        handlePickFile(filteredFilenames[pickerIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        closePicker();
        return;
      }
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    setDraft(next);
    const cursor = e.target.selectionStart ?? next.length;
    setAtQuery(getAtQuery(next, cursor));
    setPickerIndex(0);
  };

  const handleTextareaSelect = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const node = e.currentTarget;
    const cursor = node.selectionStart ?? draft.length;
    setAtQuery(getAtQuery(draft, cursor));
  };

  const hasStreamingThinking = streamState.streamingThinking.length > 0;
  // `stopped` keeps a halted answer on screen until the refetch swaps in the
  // interrupted row the backend persisted — without it the bubble blanks for
  // the half-second in between.
  const hasLiveAnswer = streamState.pending || streamState.stopped;
  const showStreamingBubble = hasLiveAnswer && streamState.streamingText.length > 0;
  const showThinking =
    streamState.pending && streamState.streamingText.length === 0 && !hasStreamingThinking;

  return (
    <>
      <div className="flex flex-1 flex-col">
      <div className="border-b px-4 py-2">
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm font-medium">{session.title}</div>
          <label className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
            <Globe className="h-3.5 w-3.5" />
            <span>Search the web</span>
            <Switch
              checked={webSearchEnabled}
              onCheckedChange={handleWebSearchToggle}
              aria-label="Search the web"
            />
          </label>
        </div>
        <div className="text-xs text-muted-foreground">
          Started by {session.createdByEmail ?? "Unknown"}
          {filtersActive && (
            <>
              {" · "}
              <span className="font-medium text-brand-teal">
                Querying {matchedDocumentIds.length} of {totalActiveDocs} studies
              </span>
            </>
          )}
        </div>
      </div>
      <div ref={scrollRef} onScroll={handleScroll} className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading messages…</div>
        ) : messages.length === 0 && !streamState.pending ? (
          <div className="text-sm text-muted-foreground">
            No messages yet. Ask a question about the project's documents to get started.
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                currentUserEmail={currentUserEmail}
                documentsByFilename={documentsByFilename}
              />
            ))}
            {hasLiveAnswer && hasStreamingThinking && (
              <ThinkingDisclosure text={streamState.streamingThinking} streaming />
            )}
            {showStreamingBubble && (
              <div className="flex flex-col items-start gap-1">
                {streamState.route && <RouteBadge route={streamState.route} />}
                <StreamingAssistantBubble text={streamState.streamingText} />
              </div>
            )}
            {showThinking && (
              <div className="flex items-center gap-2">
                <PulsingDots />
                {streamState.route && <RouteBadge route={streamState.route} />}
              </div>
            )}
            {streamState.error && (
              <div className="text-sm text-destructive">{streamState.error}</div>
            )}
          </div>
        )}
      </div>
      <div className="border-t p-3">
        <div ref={inputWrapperRef} className="relative flex items-end gap-2">
          {pickerOpen && (
            <FilePickerPopover
              filenames={filteredFilenames}
              activeIndex={pickerIndex}
              onSelect={handlePickFile}
              onClose={closePicker}
              anchorRef={inputWrapperRef}
            />
          )}
          <Textarea
            ref={textareaRef}
            value={draft}
            onChange={handleChange}
            onSelect={handleTextareaSelect}
            onKeyDown={handleKeyDown}
            placeholder="Ask a question about this project's documents… (type @ to reference a file)"
            rows={2}
            className="min-h-[44px] resize-none"
            disabled={streamState.pending}
          />
          {streamState.pending ? (
            // Without this, walking away from the page was the only way to
            // abandon a runaway answer.
            <Button
              onClick={cancel}
              variant="outline"
              aria-label="Stop generating"
              title="Stop generating"
            >
              <Square className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSend}
              disabled={!draft.trim() || noMatches}
              aria-label="Send message"
              className="bg-brand-teal text-white hover:bg-brand-teal/90"
            >
              <Send className="h-4 w-4" />
            </Button>
          )}
        </div>
        <div className="mt-1 text-xs text-muted-foreground">
          {streamState.pending ? (
            <>
              Generating… you can switch steps or sessions without losing this
              response · press Stop to cancel
            </>
          ) : noMatches ? (
            <span className="text-destructive">
              No studies match the current filters — clear filters to send.
            </span>
          ) : (
            <>
              Enter to send · Shift+Enter for a new line · Type{" "}
              <span className="font-medium">@</span> to reference a file
            </>
          )}
        </div>
      </div>
      </div>

      <ProjectChatFilters
        filterFields={filterFields}
        selection={filterSelection}
        onSelectionChange={setFilterSelection}
        totalCount={totalActiveDocs}
        matchedCount={matchedDocumentIds.length}
      />
    </>
  );
};

interface FilePickerPopoverProps {
  filenames: string[];
  activeIndex: number;
  onSelect: (filename: string) => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement>;
}

const FilePickerPopover = ({
  filenames,
  activeIndex,
  onSelect,
  onClose,
  anchorRef,
}: FilePickerPopoverProps) => {
  const listRef = useRef<HTMLDivElement>(null);

  // Keep the highlighted row visible inside the scrolling popover.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-index="${activeIndex}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  // Dismiss on outside click — the anchor itself is excluded so a click on the
  // textarea (to move the cursor) doesn't immediately close the picker.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const list = listRef.current;
      const anchor = anchorRef.current;
      if (!list || !anchor) return;
      if (list.contains(e.target as Node)) return;
      if (anchor.contains(e.target as Node)) return;
      onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose, anchorRef]);

  return (
    <div
      ref={listRef}
      className="absolute bottom-full left-0 right-0 z-50 mb-2 max-h-64 overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md"
    >
      <div className="border-b px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Project files
      </div>
      <ul>
        {filenames.map((name, i) => (
          <li key={name}>
            <button
              type="button"
              data-index={i}
              onMouseDown={(ev) => {
                // mousedown (not click) so the textarea doesn't lose focus
                // before the selection is applied.
                ev.preventDefault();
                onSelect(name);
              }}
              className={cn(
                "flex w-full items-center gap-2 px-3 py-2 text-left text-sm",
                i === activeIndex ? "bg-accent" : "hover:bg-accent/60",
              )}
            >
              <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="flex-1 truncate">{name}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Small badge showing the retrieval route returned by the classifier API.
const RouteBadge = ({ route }: { route: string }) => (
  <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
    {route.toUpperCase()}
  </span>
);

// Waiting-for-first-token indicator, shown before any thinking or answer
// text has arrived.
const PulsingDots = () => (
  <div className="flex items-center gap-1 py-2" aria-label="Assistant is responding">
    <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:-0.3s]" />
    <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:-0.15s]" />
    <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60" />
  </div>
);

// Collapsible reasoning trace shown above an assistant turn. Expanded by
// default while it's actively streaming in — that's the moment it's most
// useful to watch — then collapses for historical/persisted messages, since
// the thinking text can be long and most users just want the answer.
const ThinkingDisclosure = ({ text, streaming }: { text: string; streaming?: boolean }) => {
  const [expanded, setExpanded] = useState(() => !!streaming);
  if (!text) return null;
  return (
    <div className="mb-1 max-w-[85%]">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        {streaming ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <Brain className="h-3 w-3" />
        )}
        <span>{streaming ? "Thinking…" : "Thinking"}</span>
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>
      {expanded && (
        <div className="mt-1 max-h-64 overflow-y-auto whitespace-pre-wrap rounded-md border border-dashed px-3 py-2 text-xs italic text-muted-foreground">
          {text}
        </div>
      )}
    </div>
  );
};

// No "Assistant" label/spinner header here while streaming — the thinking
// row (when present) or the pulsing dots already signal "in progress"; the
// label appears once the message is persisted and gets a real timestamp.
const StreamingAssistantBubble = ({ text }: { text: string }) => (
  <div className="flex flex-col items-start">
    <div className="max-w-[85%] rounded-lg bg-muted px-3 py-2 text-sm">
      <div className="prose prose-sm dark:prose-invert max-w-none">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
      </div>
    </div>
  </div>
);

interface MessageBubbleProps {
  message: ChatMessage;
  currentUserEmail: string | undefined;
  documentsByFilename: Map<string, Document>;
}

const MessageBubble = ({
  message,
  currentUserEmail,
  documentsByFilename,
}: MessageBubbleProps) => {
  const isAssistant = message.role === "assistant";
  const label = senderLabel(message, currentUserEmail);
  const isOwnMessage = label === "You";
  const { openDocument } = useDocumentViewer();

  // Build a lookup so inline [N] pills can resolve their citation in O(1).
  const citationsById = useMemo(() => {
    const map = new Map<string, ChatCitation>();
    (message.citations ?? []).forEach((c) => map.set(c.id, c));
    return map;
  }, [message.citations]);

  const openCitation = useCallback(
    (citationId: string) => {
      const citation = citationsById.get(citationId);
      if (!citation) return;
      const doc = findDocBySourcefile(citation.sourcefile, documentsByFilename);
      if (!doc) return;
      const pageNum = extractPageNumber(citation.sourcepage);
      const page = pageNum ? `#page=${pageNum}` : "";
      openDocument({
        doc,
        page,
        sourceText: citation.content,
        side: "right",
        showTabs: true,
      });
    },
    [citationsById, documentsByFilename, openDocument],
  );

  // react-markdown renders citation pills via an <a href="#cite-N"> override
  // injected by linkifyCitations(). Pre-process only when there are citations
  // to render and rendering as markdown (assistant role).
  const renderableContent = useMemo(
    () => (isAssistant && message.citations ? linkifyCitations(message.content) : message.content),
    [isAssistant, message.citations, message.content],
  );

  const markdownComponents = useMemo(
    () => ({
      a: ({ href, children, ...rest }: AnchorRenderProps) => {
        if (typeof href === "string" && href.startsWith(CITATION_HREF_PREFIX)) {
          const id = href.slice(CITATION_HREF_PREFIX.length);
          const resolved = citationsById.has(id);
          return (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                openCitation(id);
              }}
              disabled={!resolved}
              className={cn(
                "mx-0.5 inline-flex items-baseline rounded-sm px-1 align-baseline text-xs font-medium",
                resolved
                  ? "bg-brand-teal/10 text-brand-teal hover:bg-brand-teal/20"
                  : "bg-muted text-muted-foreground cursor-not-allowed",
              )}
            >
              [{children}]
            </button>
          );
        }
        // Web-sourced answers self-report citations as ordinary markdown
        // links whose text is just the citation number, e.g.
        // "[1](https://example.com)" — style those as pills too, opening
        // the URL in a new tab instead of the in-app PDF viewer.
        if (
          message.webSearchStatus === "ok" &&
          typeof href === "string" &&
          /^https?:\/\//.test(href) &&
          /^\d+$/.test(childrenToText(children).trim())
        ) {
          return (
            <button
              type="button"
              onClick={() => window.open(href, "_blank", "noopener,noreferrer")}
              title={href}
              className="mx-0.5 inline-flex items-baseline rounded-sm bg-blue-500/10 px-1 align-baseline text-xs font-medium text-blue-600 hover:bg-blue-500/20 dark:text-blue-400"
            >
              [{children}]
            </button>
          );
        }
        return (
          <a href={href} {...rest}>
            {children}
          </a>
        );
      },
    }),
    [citationsById, openCitation, message.webSearchStatus],
  );

  return (
    <div className={cn("flex flex-col", isOwnMessage ? "items-end" : "items-start")}>
      <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
        <span className="font-medium">{label}</span>
        <span>·</span>
        <span>{formatTime(message.createdAt)}</span>
        {isAssistant && message.route && <RouteBadge route={message.route} />}
      </div>
      {isAssistant && message.thinking && <ThinkingDisclosure text={message.thinking} />}
      {isAssistant && message.webSearchStatus === "ok" && (
        <div className="mb-1 flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
          <Globe className="h-3 w-3" />
          <span>Answered from the web</span>
        </div>
      )}
      <div
        className={cn(
          "max-w-[85%] rounded-lg px-3 py-2 text-sm",
          isAssistant
            ? "bg-muted"
            : isOwnMessage
              ? "bg-brand-teal text-white"
              : "bg-accent",
        )}
      >
        {isAssistant ? (
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {renderableContent}
            </ReactMarkdown>
          </div>
        ) : (
          <span className="whitespace-pre-wrap">{message.content}</span>
        )}
      </div>
      {isAssistant && message.citations && message.citations.length > 0 && (
        <CitationChips
          citations={message.citations}
          documentsByFilename={documentsByFilename}
        />
      )}
      {isAssistant && message.webSearchStatus === "ok" && message.webCitations
        && message.webCitations.length > 0 && (
        <WebCitationChips citations={message.webCitations} />
      )}
      {isAssistant && message.appliedFilters && (
        <AppliedFiltersFooter applied={message.appliedFilters} />
      )}
      {isAssistant && message.webSearchStatus === "error" && (
        <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
          <Globe className="h-3 w-3" />
          <span>Web search couldn't complete this time — answered from your documents instead.</span>
        </div>
      )}
      {isAssistant && message.status === "interrupted" && (
        // A truncated answer must never read as a finished one — this is
        // evidence work, and the missing half is invisible without a marker.
        <div className="mt-1 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-500">
          <Square className="h-3 w-3" />
          <span>Response interrupted — this answer is incomplete.</span>
        </div>
      )}
    </div>
  );
};

interface AppliedFiltersFooterProps {
  applied: AppliedFilters;
}

const AppliedFiltersFooter = ({ applied }: AppliedFiltersFooterProps) => {
  const { items, matchedCount, totalCount } = applied;
  // Server returns null when no filters were applied — but the assistant
  // message may also persist an empty items list defensively. Hide the
  // footer in that case so the chat looks identical to pre-feature output.
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
      <span className="font-medium">Applied filters:</span>
      {items.map((item) => (
        <span
          key={item.key}
          title={`${item.label}: ${item.values.join(", ")}`}
          className="inline-flex items-center gap-1 rounded-full border border-brand-navy/30 bg-brand-navy/5 px-2 py-0.5 text-brand-navy"
        >
          <span className="font-medium">{item.label}:</span>
          <span className="max-w-[160px] truncate">{item.values.join(", ")}</span>
        </span>
      ))}
      <span>
        · Queried {matchedCount} of {totalCount} studies
      </span>
    </div>
  );
};

interface AnchorRenderProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  children?: React.ReactNode;
}

interface CitationChipsProps {
  citations: ChatCitation[];
  documentsByFilename: Map<string, Document>;
}

/**
 * Resolve a citation's sourcefile to a project Document, tolerating minor
 * filename mismatches (different casing, leading path prefix) that can occur
 * when the blob name used at indexing time differs from the project document
 * filename stored in the evidence-base.
 */
function findDocBySourcefile(
  sourcefile: string,
  documentsByFilename: Map<string, Document>,
): Document | undefined {
  const direct = documentsByFilename.get(sourcefile);
  if (direct) return direct;
  const normSource = sourcefile.split(/[/\\]/).pop()?.toLowerCase();
  if (!normSource) return undefined;
  for (const [key, doc] of documentsByFilename.entries()) {
    if (key.split(/[/\\]/).pop()?.toLowerCase() === normSource) return doc;
  }
  return undefined;
}

/**
 * Extract @filename tokens from message text and resolve them to document IDs.
 * Returns an array of IDs when ≥1 mention is found AND every mention resolves
 * to a known project document. Returns null if there are no mentions (caller
 * should fall back to the full filtered document set).
 */
function parseAtMentionIds(
  text: string,
  documentsByFilename: Map<string, Document>,
): string[] | null {
  const mentions = Array.from(text.matchAll(/@(\S+)/g), (m) => m[1]);
  if (mentions.length === 0) return null;
  const ids: string[] = [];
  for (const mention of mentions) {
    const doc = findDocBySourcefile(mention, documentsByFilename);
    if (!doc) return null; // unresolvable mention — don't restrict scope
    ids.push(doc.id);
  }
  return [...new Set(ids)];
}

function buildCitationTitle(
  c: ChatCitation,
  pageNum: number | null,
  resolved: boolean,
): string {
  if (!resolved) return `${c.sourcefile} is not in this project's active documents`;
  const pageSuffix = pageNum ? ` at page ${pageNum}` : "";
  return `Open ${c.sourcefile}${pageSuffix}`;
}

const CitationChips = ({ citations, documentsByFilename }: CitationChipsProps) => {
  const { openDocument } = useDocumentViewer();

  const handleOpen = (citation: ChatCitation, pageNum: number | null) => {
    const doc = findDocBySourcefile(citation.sourcefile, documentsByFilename);
    if (!doc) return;
    const page = pageNum ? `#page=${pageNum}` : "";
    openDocument({
      doc,
      page,
      sourceText: citation.content,
      side: "right",
      showTabs: true,
    });
  };

  return (
    <div className="mt-2 flex flex-wrap gap-1">
      {citations.map((c) => {
        const resolved = !!findDocBySourcefile(c.sourcefile, documentsByFilename);
        const pageNum = extractPageNumber(c.sourcepage);
        const title = buildCitationTitle(c, pageNum, resolved);
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => handleOpen(c, pageNum)}
            disabled={!resolved}
            title={title}
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs",
              resolved
                ? "border-brand-teal/40 bg-brand-teal/5 text-brand-teal hover:bg-brand-teal/10"
                : "border-muted text-muted-foreground cursor-not-allowed",
            )}
          >
            <FileText className="h-3 w-3" />
            <span className="font-medium">[{c.id}]</span>
            <span className="max-w-[160px] truncate">{c.sourcefile}</span>
            {pageNum !== null && pageNum > 0 ? <span>· p.{pageNum}</span> : null}
          </button>
        );
      })}
    </div>
  );
};

interface WebCitationChipsProps {
  citations: WebCitation[];
}

// Web citations only carry a URL — show its hostname as a readable label
// rather than the full (often long) URL.
function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

const WebCitationChips = ({ citations }: WebCitationChipsProps) => (
  <div className="mt-2 flex flex-wrap gap-1">
    {citations.map((c) => (
      <button
        key={c.id}
        type="button"
        onClick={() => window.open(c.url, "_blank", "noopener,noreferrer")}
        title={c.url}
        className="inline-flex items-center gap-1 rounded-full border border-blue-500/40 bg-blue-500/5 px-2 py-0.5 text-xs text-blue-600 hover:bg-blue-500/10 dark:text-blue-400"
      >
        <Globe className="h-3 w-3" />
        <span className="font-medium">[{c.id}]</span>
        <span className="max-w-[160px] truncate">{hostnameOf(c.url)}</span>
      </button>
    ))}
  </div>
);

export default ProjectChat;
